import React, { useCallback, useEffect, useRef } from "react";
import { useDefinition } from "../../../hooks/useDefinition";

import { useLabels } from "../../../hooks/useLabels";
import { useTextZoneDashboard } from "../../../hooks/useTextZoneDashboard";
import { TextField } from "../../text-field";
import { labels } from "./labels";
import { useUserDateDialogActions, useUserDateDialogState } from "./use-user-date-dialog-state";

import "./user-date-form.css";

export const UserDateForm = () => {
	const [[textInputPlaceholderLabel, dayNumberLabel, monthNumberLabel]] = useLabels([
		labels.eventTitle,
		labels.dayNumber,
		labels.monthNumber,
	]);
	const dashboard = useTextZoneDashboard();
	const definition = useDefinition();

	const formEdit = useRef<HTMLInputElement>(null);
	const { setFormValues } = useUserDateDialogActions();
	const { formValues, selectedUserDate } = useUserDateDialogState();

	const updateText = useCallback(
		(requestedValue: string) => {
			const strippedAndReplacedText = dashboard.stripUnsupportedCharacters(
				requestedValue,
				definition?.supportedCharSet,
				1, // each event is 1 line, but can be combined to multiple lines in the textZone depending on maxNumberOfEvents,
				undefined,
			).newText;
			setFormValues({ ...formValues, title: strippedAndReplacedText });
		},
		[dashboard, definition?.supportedCharSet, formValues, setFormValues],
	);

	useEffect(() => {
		if (selectedUserDate) {
			setFormValues({
				day: eXpress.RecurringDateCode.getDay(selectedUserDate.dateCode),
				month: eXpress.RecurringDateCode.getMonth(selectedUserDate.dateCode),
				title: selectedUserDate?.value,
			});
			formEdit.current?.scrollIntoView();
		}
	}, [selectedUserDate, selectedUserDate?.dateCode, selectedUserDate?.value, setFormValues]);

	return (
		<form id="event-form" className="user-date-form">
			<div className="user-date-form__left">
				<TextField
					className="user-date-form__input"
					value={formValues.title || ""}
					required
					name={textInputPlaceholderLabel}
					placeholder={textInputPlaceholderLabel}
					onChange={(e) => updateText(e.currentTarget.value)}
					ref={formEdit}
				/>
			</div>
			<div className="user-date-form__right">
				<select
					required
					value={formValues.day || ""}
					name={dayNumberLabel}
					placeholder={dayNumberLabel}
					onChange={(e) =>
						setFormValues({
							...formValues,
							day: +e.currentTarget.value,
						})
					}
				>
					<option value="" disabled>
						{dayNumberLabel}
					</option>
					{[
						...Array(
							eXpress.creator.calendars.calendarUtils.daysInMonth[
								(formValues?.month || 1) - 1
							],
						),
					].map((_, v: number) => {
						return (
							// eslint-disable-next-line react/no-array-index-key
							<option key={v} value={v + 1}>
								{v + 1}
							</option>
						);
					})}
				</select>
				<select
					required
					value={formValues?.month || ""}
					name={monthNumberLabel}
					placeholder={monthNumberLabel}
					onChange={(e) => {
						const month = +e.currentTarget.value;
						const lastDayOfMonth =
							eXpress.creator.calendars.calendarUtils.daysInMonth[(month || 1) - 1];
						const day =
							formValues.day && lastDayOfMonth && formValues.day > lastDayOfMonth
								? 0
								: formValues?.day;
						setFormValues({ ...formValues, month, day });
					}}
				>
					<option value="" disabled>
						{monthNumberLabel}
					</option>
					{eXpress.creator.calendars.calendarUtils.daysInMonth.map((_, index: number) => (
						// eslint-disable-next-line react/no-array-index-key
						<option key={index} value={index + 1}>
							{index + 1}
						</option>
					))}
				</select>
			</div>
		</form>
	);
};
